/**
 * TanStack Query hook for customer ticket detail.
 *
 * Wraps GET /api/v1/tickets/{number}. The poll interval is wired in by
 * the page component (SPEC §8 says 15 seconds for the conversation
 * thread) - this hook just exposes the query.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { AttachmentResponse, TicketResponse } from "@/types/api";

export function ticketDetailQueryKey(
  code: string | number,
): readonly unknown[] {
  // Lowercase the cache key so the SAME ticket fetched as "KU1" and
  // "ku1" hits one cache entry. Numbers pass through unchanged.
  const key = typeof code === "string" ? code.toLowerCase() : code;
  return ["tickets", "detail", key] as const;
}

export function ticketAttachmentsQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["tickets", "attachments", ticketNumber] as const;
}

export interface UseTicketDetailOptions {
  /** Pause polling while a modal is open. Defaults to true. */
  enabled?: boolean;
  /** Override the 15s poll interval; pass false to disable. */
  refetchIntervalMs?: number | false;
}

/**
 * Customer-side ticket detail fetch. ``code`` is the user-facing
 * identifier from the URL - any of:
 *   - native display_code (case-insensitive, e.g. "ku1")
 *   - FD-prefix form (e.g. "f-104944")
 *   - bare ticket_number (back-compat for old links)
 * The backend resolves all three forms via get_ticket_by_public_code.
 * Once loaded, the consumer reads ``ticket.ticket_number`` for any
 * further API calls (comments, attachments, etc.); the code-based
 * URL is only used for THIS bootstrap fetch.
 */
export function useTicketDetail(
  code: string,
  options: UseTicketDetailOptions = {},
): UseQueryResult<TicketResponse> {
  return useQuery({
    queryKey: ticketDetailQueryKey(code),
    enabled: options.enabled !== false && code.length > 0,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    queryFn: async () => {
      const response = await api.get<TicketResponse>(
        `/tickets/${encodeURIComponent(code)}`,
      );
      return response.data;
    },
  });
}

export function useTicketAttachments(
  ticketNumber: number,
  options: UseTicketDetailOptions = {},
): UseQueryResult<AttachmentResponse[]> {
  return useQuery({
    queryKey: ticketAttachmentsQueryKey(ticketNumber),
    enabled: options.enabled !== false,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    queryFn: async () => {
      const response = await api.get<AttachmentResponse[]>(
        `/tickets/${ticketNumber}/attachments`,
      );
      return response.data;
    },
  });
}

/**
 * Customer download-URL fetcher. Mirrors the agent flow at
 * fetchAgentAttachmentDownloadUrl: hits the JSON endpoint so the JWT
 * Bearer header rides via axios (an anchor click does NOT carry the
 * header, which is why the sibling 302-redirect ``/download`` endpoint
 * can't drive a customer-facing UI). Returns the short-lived signed
 * Spaces URL that the caller can hand to a programmatic anchor click.
 */
export async function fetchCustomerAttachmentDownloadUrl(
  ticketNumber: number,
  attachmentId: string,
): Promise<string> {
  const response = await api.get<{ url: string }>(
    `/tickets/${ticketNumber}/attachments/${attachmentId}/download-url`,
  );
  return response.data.url;
}
